<template>
  <div class="sidebar-content p-2 persona-switcher-sidebar">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Personas</h5>
      <feather-icon
        icon="XIcon"
        size="20"
        class="cursor-pointer"
        @click="$emit('close-switcher', false);"
      />
    </div>

    <!-- Selected Persona Section -->
    <div class="d-flex justify-content-between align-items-center mt-2 mb-05">
      <p class="mb-0">{{ $t('ia.assistants.selectedPersona') }}</p>
    </div>
    <div class="select-persona empty-state" v-if="!assistant">
      <div class="d-flex align-items-center text-center">
        <feather-icon icon="UserIcon" size="32" class="text-muted" />
        <div class="d-flex flex-column ml-1">
          <p class="avenir-medium m-0 mb-05">{{ $t('ia.assistants.noAssistantSelected') }}</p>
          <p class="avenir-light m-0 text-muted small">{{ $t('ia.assistants.selectAssistantToStart') }}</p>
        </div>
      </div>
    </div>
    <div v-else class="select-persona mb-2">
      <div class="d-flex align-items-center mb-05">
        <b-avatar :src="assistant.image ? getAssetUrl(assistant.image) : assistant.image_url"></b-avatar>
        <div class="d-flex flex-column ml-05">
          <strong>{{ assistant.name }}</strong>
          <p class="avenir-light m-0">{{ $t('ia.type_assistant').find((i) => i.value === assistant.type_assistant).text }}</p>
        </div>
      </div>
    </div>

    <!-- Public Personas Section -->
    <div class="persona-section mb-2">
      <div class="d-flex justify-content-between align-items-center mt-2 mb-05">
        <p class="mb-0 text-uppercase">{{ $t('ia.assistants.publicPersona') }}</p>
      </div>
      <div v-if="loadingPublic" class="py-1 personas-list">
        <div v-for="n in 3" :key="'public-skeleton-'+n" class="select-persona skeleton-card mb-05">
          <div class="d-flex align-items-center">
            <div class="persona-avatar mr-2">
              <b-skeleton type="avatar" size="40px" />
            </div>
            <div class="flex-grow-1">
              <b-skeleton width="60%" height="14px" class="mb-1" />
              <b-skeleton width="40%" height="12px" />
            </div>
            <b-skeleton type="button" width="36px" height="32px" class="ml-1" />
          </div>
        </div>
      </div>
      <div v-else-if="publicPersonas.length === 0" class="text-center text-muted py-3">
        <small>{{ $t('ia.assistants.noPublicPersonas') }}</small>
      </div>
      <div v-else class="personas-list">
        <div
          v-for="persona in publicPersonas"
          :key="persona.uuid"
          class="select-persona mb-05"
        >
          <div class="d-flex align-items-center">
            <div class="persona-avatar mr-2">
              <img
                v-if="persona.image_url"
                :src="persona.image_url"
                :alt="persona.name"
                class="rounded-circle"
                width="40"
                height="40"
              />
              <feather-icon v-else icon="UserIcon" size="40" />
            </div>
            <div class="flex-grow-1">
              <div class="persona-name">{{ persona.name }}</div>
              <p class="avenir-light m-0">{{ $t('ia.type_assistant').find((i) => i.value === persona.type_assistant).text }}</p>
            </div>
            <b-button
              variant="flat-primary"
              size="sm"
              class="btn-icon"
              @click="activateAssistantMethod(persona.uuid)"
            >
              <feather-icon icon="PlusIcon" size="18" />
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Private Personas Section -->
    <div class="persona-section ">
      <div class="d-flex justify-content-between align-items-center mb-05">
        <p class="mb-0 text-uppercase">{{ $t('ia.assistants.privatePersona') }}</p>
      </div>
      <div v-if="loadingPrivate" class="py-1 personas-list">
        <div v-for="n in 2" :key="'private-skeleton-'+n" class="select-persona skeleton-card mb-05">
          <div class="d-flex align-items-center">
            <div class="persona-avatar mr-2">
              <b-skeleton type="avatar" size="40px" />
            </div>
            <div class="flex-grow-1">
              <b-skeleton width="60%" height="14px" class="mb-1" />
              <b-skeleton width="40%" height="12px" />
            </div>
            <b-skeleton type="button" width="36px" height="32px" class="ml-1" />
          </div>
        </div>
      </div>
      <div v-else-if="privatePersonas.length === 0" class="text-center text-muted py-3">
        <small>{{ $t('ia.assistants.noPrivatePersonas') }}</small>
      </div>
      <div v-else class="personas-list">
        <div
          v-for="persona in privatePersonas"
          :key="persona.uuid"
          class="select-persona mb-05"
        >
          <div class="d-flex align-items-center">
            <div class="persona-avatar mr-2">
              <img
                v-if="persona.image_url"
                :src="persona.image_url"
                :alt="persona.name"
                class="rounded-circle"
                width="40"
                height="40"
              />
              <feather-icon v-else icon="UserIcon" size="40" />
            </div>
            <div class="flex-grow-1">
              <div class="persona-name">{{ persona.name }}</div>
              <p class="avenir-light m-0">{{ $t('ia.type_assistant').find((i) => i.value === persona.type_assistant).text }}</p>
            </div>
            <b-button
              variant="flat-primary"
              size="sm"
              class="btn-icon"
              @click="activateAssistantMethod(persona.uuid)"
            >
              <feather-icon icon="PlusIcon" size="18" />
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Button -->
    <div class="text-center">
      <b-button
        variant="blue-button"
        class="blue-button"
        block
        @click="navigateToExplorePersonas"
      >
        <feather-icon icon="PlusCircleIcon" size="16" class="mr-50" />
        {{ $t('ia.assistants.addManagePersonas') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BButton, BAvatar, BSkeleton
} from 'bootstrap-vue';
import { getAssistants, activateAssistant } from '@/services/ia';
import { getAssetUrl } from '@/libs/utils/urls';
import { showToast } from '@/libs/utils/others';

export default {
  name: 'PersonaSwitcherSidebar',
  components: {
    BButton,
    BAvatar,
    BSkeleton,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    assistant: {
      type: Object,
      default: null,
    },
    categorySlug: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      getAssetUrl,
      publicPersonas: [],
      privatePersonas: [],
      loadingPublic: false,
      loadingPrivate: false,
      activatingPersona: false,
    };
  },
  mounted() {
    this.loadAssistants({ isPublic: false });
    this.loadAssistants({ isPublic: true });
  },
  methods: {
    async loadAssistants({ isPublic }) {
      const loadingKey = isPublic ? 'loadingPrivate' : 'loadingPublic';
      const listKey = isPublic ? 'privatePersonas' : 'publicPersonas';
      this[loadingKey] = true;
      try {
        const resp = await getAssistants({}, isPublic === false ? false : undefined);
        const results = resp?.results || [];
        this[listKey] = this.assistant ? results.filter(p => p.uuid !== this.assistant.uuid) : results;
        this[loadingKey] = false;
      } catch (e) {
        console.error('Error loading personas:', e);
      } finally {
        this[loadingKey] = false;
      }
    },
    activateAssistantMethod(person_assistant_uuid) {
      activateAssistant({ person_assistant_uuid }).then((response) => {
        showToast(this, 'success', 'CheckIcon', 'Persona activated successfully');

        this.loadAssistants({ isPublic: false });
        this.loadAssistants({ isPublic: true });
        this.$emit('update-settings', response);
      });
    },
    navigateToExplorePersonas() {
      this.$emit('navigate-to-explore');
    },
  },
};
</script>
<style lang="scss" scoped>
.persona-switcher-sidebar {

  .select-persona {
    background-color: rgb(243, 243, 243);
    padding: 0.6em;
    border-radius: 5px;
    border: 0.4px solid #ccc;

    &.empty-state {
      background-color: #fafafa;
      border: 1px dashed #d1d5db;
    }
  }
  .sidebar-content {
    height: 100%;
    overflow-y: auto;
  }

  .section-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #5e5873;
    margin-bottom: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .persona-section {
    padding-bottom: 1rem;

    &:last-of-type {
      border-bottom: none;
    }
  }

  .persona-card {
    padding: 0.75rem;
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
    transition: all 0.2s ease;
    cursor: default;

    &:hover {
      background-color: #f8f8f8;
    }

    &.selected {
      background-color: #f0f9ff;
      border: 1px solid #3b82f6;
    }

    &.is-active {
      background-color: #f0fdf4;
      border: 1px solid #10b981;
    }

    .persona-avatar {
      flex-shrink: 0;

      img {
        object-fit: cover;
      }
    }

    .persona-name {
      font-weight: 600;
      font-size: 0.95rem;
      color: #5e5873;
      margin-bottom: 0.25rem;
    }
  }

  // Skeleton styles
  .skeleton-card {
    .b-skeleton {
      animation: skeleton-loading 1.4s ease-in-out infinite alternate;
    }
  }
  @keyframes skeleton-loading {
    0% { opacity: 0.6; }
    100% { opacity: 1; }
  }

  .personas-list {
    max-height: 300px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 10px;

      &:hover {
        background: #a8a8a8;
      }
    }
  }

  .cursor-pointer {
    cursor: pointer;
  }
}
</style>
